// src/hooks/useFormDraft.ts
import { useEffect, useMemo, useRef, useState } from 'react';

import debounce from 'lodash/debounce';

import { useAppDispatch, useAppSelector } from '../store/hooks';
import { loadDraftAnswers, restoreDraftFindings } from '../store/modules/webForms.slice';

interface UseFormDraftOptions {
  formId: string | undefined;
  userKey: string | undefined;
  editMode: boolean;
  formLoaded: boolean;
}

interface UseFormDraftResult {
  hasDraft: boolean;
  loadDraft: () => void;
  discardDraft: () => void;
  clearDraft: () => void;
}

interface DraftPayload {
  savedAt: number;
  answers: Record<string, { value: unknown }>;
  // Finding ids created during this draft session. Persisted alongside answers
  // so a page reload + draft restore re-links findings to the new FormAnswer
  // instead of leaving them orphaned in Firestore.
  findingIds?: string[];
}

const SEVEN_DAYS_MS = 7 * 24 * 60 * 60 * 1000;
const DEBOUNCE_MS = 1500;
// Serialized default shape of an empty repeatable/table question
const EMPTY_TABLE_VALUE = JSON.stringify({ 0: {} });

function storageKey(formId: string, userKey: string): string {
  return `draft_form_${formId}_${userKey}`;
}

function readDraft(key: string): DraftPayload | null {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const payload: DraftPayload = JSON.parse(raw);
    if (!payload?.answers || typeof payload.answers !== 'object') return null;
    if (Date.now() - payload.savedAt > SEVEN_DAYS_MS) {
      localStorage.removeItem(key);
      return null;
    }
    return payload;
  } catch {
    return null;
  }
}

/** Returns true if the value is a non-default, user-provided answer. */
function hasUserValue(value: unknown): boolean {
  if (value === undefined || value === null) return false;
  if (typeof value === 'string' && value.trim() === '') return false;
  if (typeof value === 'object' && JSON.stringify(value) === EMPTY_TABLE_VALUE) return false;
  return true;
}

export function useFormDraft({
  formId,
  userKey,
  editMode,
  formLoaded,
}: UseFormDraftOptions): UseFormDraftResult {
  const dispatch = useAppDispatch();
  const answers = useAppSelector((state) => state.webForms.answers);
  const findingsCreated = useAppSelector((state) => state.webForms.findingsCreated);

  const enabled = formLoaded && !editMode && !!formId && !!userKey;
  const key = formId && userKey ? storageKey(formId, userKey) : null;

  const [hasDraft, setHasDraft] = useState(false);
  // Track whether the draft dialog has been resolved to allow auto-save
  const dialogResolvedRef = useRef(false);

  // On mount (once form is loaded), check for a valid draft
  useEffect(() => {
    if (!enabled || !key) return;
    const draft = readDraft(key);
    if (draft) {
      setHasDraft(true);
    } else {
      dialogResolvedRef.current = true;
    }
  }, [enabled, key]);

  const answersRef = useRef(answers);
  useEffect(() => {
    answersRef.current = answers;
  }, [answers]);

  const findingsCreatedRef = useRef(findingsCreated);
  useEffect(() => {
    findingsCreatedRef.current = findingsCreated;
  }, [findingsCreated]);

  const debouncedSave = useMemo(() => {
    return debounce(() => {
      if (!key || !dialogResolvedRef.current) return;
      // Don't save if the form is still in its default/reset state (no user input yet).
      // This prevents creating ghost drafts after resetForm() or right after form load.
      const hasAnyUserValue = Object.values(answersRef.current).some((a) =>
        hasUserValue(a.value),
      );
      if (!hasAnyUserValue) return;
      // Filter to create-mode findings for this form so we never persist entries
      // that belong to a different form or to an edit session.
      const findingIds = findingsCreatedRef.current
        .filter((entry) => entry.formId === formId && entry.formAnswerId === null)
        .map((entry) => entry.findingId);
      const payload: DraftPayload = {
        savedAt: Date.now(),
        answers: Object.fromEntries(
          Object.entries(answersRef.current).map(([qId, answer]) => [qId, { value: answer.value }]),
        ),
        findingIds,
      };
      try {
        localStorage.setItem(key, JSON.stringify(payload));
      } catch {
        // localStorage might be unavailable (private mode, quota exceeded)
      }
    }, DEBOUNCE_MS);
  }, [key, formId]);

  // Auto-save whenever answers or the tracked findings change. Creating a
  // finding also updates an answer's finding_created flag, but depending on
  // findingsCreated directly is more robust to future call sites.
  useEffect(() => {
    if (!enabled) return;
    debouncedSave();
    return () => { debouncedSave.cancel(); };
  }, [answers, findingsCreated, enabled, debouncedSave]);

  const loadDraft = () => {
    if (!key) return;
    const draft = readDraft(key);
    if (draft) {
      dispatch(loadDraftAnswers(draft.answers));
      // Re-attach the finding ids saved during the previous session so the next
      // submit batch can link them. Without this, those findings would stay in
      // Firestore with no form_answer set after a reload-and-restore.
      if (formId && draft.findingIds && draft.findingIds.length > 0) {
        dispatch(restoreDraftFindings({ formId, findingIds: draft.findingIds }));
      }
    }
    dialogResolvedRef.current = true;
    setHasDraft(false);
  };

  const discardDraft = () => {
    if (!key) return;
    localStorage.removeItem(key);
    dialogResolvedRef.current = true;
    setHasDraft(false);
  };

  const clearDraft = () => {
    if (!key) return;
    localStorage.removeItem(key);
    debouncedSave.cancel();
    setHasDraft(false);
  };

  return { hasDraft, loadDraft, discardDraft, clearDraft };
}
